import React, { useState, useEffect, useCallback } from 'react';
import { uebergangApi, KlasseStatusItem, SchuelerMin, WizardSummary, AttestKandidat } from '../../lib/uebergang-api';
import { apiPut } from '../../lib/api';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import { MIcon } from '../../lib/micon';
import { useOhneMikroKnopf } from '../../lib/hooks';
import { schuljahrFensterAusLabel } from '../../lib/schuljahr';

// ── Types ─────────────────────────────────────────────────────────────────────

type Wahl = 'offen' | 'promotion' | 'archivieren' | 'aufgeloest';
type Schritt = 'laden' | 'begruessung' | 'entscheidung' | 'promotion' | 'attest' | 'stundenplan' | 'bestaetigung';

interface SchuelerToggle extends SchuelerMin {
  aktiv: boolean;
}

interface AttestEntscheidung extends AttestKandidat {
  fortfuehren: boolean;
}

interface KlasseEntry {
  klasse: KlasseStatusItem;
  wahl: Wahl;
  neuer_name: string;
  gewaehlte_faecher: string[];
  schueler: SchuelerToggle[];
  schueler_geladen: boolean;
  abgeschlossen: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function suggestNeuerName(alter: string): string {
  return alter.replace(/(\d+)/, (_, n) => String(Number(n) + 1));
}

function initKlasseEntry(k: KlasseStatusItem): KlasseEntry {
  return {
    klasse: k,
    wahl: 'offen',
    neuer_name: suggestNeuerName(k.name),
    gewaehlte_faecher: [...k.faecher_unterrichtet],
    schueler: [],
    schueler_geladen: false,
    abgeschlossen: false,
  };
}

// ── Sub-component: Stepper ────────────────────────────────────────────────────

function WizardStepper({ idx, gesamt, schritt }: { idx: number; gesamt: number; schritt: Schritt }) {
  if (schritt === 'laden' || schritt === 'begruessung') return null;
  const nachKlassen = schritt === 'attest' || schritt === 'stundenplan' || schritt === 'bestaetigung';
  const fertig = nachKlassen ? gesamt : idx;
  const pct = gesamt > 0 ? Math.round((fertig / gesamt) * 100) : 0;
  const label = schritt === 'attest'
    ? 'Attestpflichten'
    : nachKlassen ? 'Alle Klassen verarbeitet' : `Klasse ${idx + 1} von ${gesamt}`;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--ld-text-secondary)', marginBottom: 6, fontWeight: 600, letterSpacing: 0.3 }}>
        <span>Schuljahres-Übergang</span>
        <span>{label}</span>
      </div>
      <div style={{ height: 4, background: '#E5E5EA', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--ld-primary)', borderRadius: 2, transition: 'width .4s ease' }} />
      </div>
    </div>
  );
}

// ── Screen: Begrüßung ─────────────────────────────────────────────────────────

function BegruessungScreen({
  aktuelles_sj, naechstes_sj, klassen_count, schueler_count, laden, onStart,
}: {
  aktuelles_sj: string; naechstes_sj: string;
  klassen_count: number; schueler_count: number;
  laden: boolean; onStart: () => void;
}) {
  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ marginBottom: 12, color: 'var(--ld-primary)' }}><MIcon name="school" size={44} /></div>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px', color: 'var(--ld-text-primary)' }}>
          Bereit für {naechstes_sj}?
        </h1>
        <p style={{ fontSize: 15, color: 'var(--ld-text-secondary)', margin: 0, lineHeight: 1.5 }}>
          Schuljahr {aktuelles_sj} läuft ab. Wir gehen deine Klassen zusammen durch.
        </p>
      </div>

      {/* Welle N2: keine Anker-Noten mehr — HJ 1 zählt ohne Vorjahresnote */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 32 }}>
        {[
          { label: 'Klassen', value: klassen_count },
          { label: 'Schüler/innen', value: schueler_count },
        ].map(({ label, value }) => (
          <div key={label} style={{ background: 'var(--ld-bg-card)', borderRadius: 'var(--ld-radius-lg)', padding: '16px 12px', textAlign: 'center', boxShadow: 'var(--ld-shadow-card)' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--ld-primary)' }}>{value}</div>
            <div style={{ fontSize: 11, color: 'var(--ld-text-secondary)', fontWeight: 600, marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      <button onClick={onStart} disabled={laden} style={{
        width: '100%', padding: '16px 0', borderRadius: 'var(--ld-radius-lg)', border: 'none',
        background: laden ? '#B8B0D4' : 'var(--ld-primary)',
        color: 'white', fontSize: 17, fontWeight: 700, cursor: laden ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      }}>
        {laden ? (
          <span style={{ width: 18, height: 18, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin .6s linear infinite' }} />
        ) : 'Loslegen →'}
      </button>
    </div>
  );
}

// ── Screen: Klassen-Entscheidung ──────────────────────────────────────────────

function KlasseEntscheidungScreen({
  entry, onWeiter, onZurueck, laden,
}: {
  entry: KlasseEntry; laden: boolean;
  onWeiter: (wahl: Wahl) => void;
  onZurueck: () => void;
}) {
  const [wahl, setWahl] = useState<Wahl>(entry.wahl === 'offen' ? 'promotion' : entry.wahl);
  const k = entry.klasse;

  const optionen: { val: Wahl; label: string; desc: string }[] = [
    {
      val: 'promotion',
      label: 'Ich übernehme diese Klasse',
      desc: `Klasse wird zu "${suggestNeuerName(k.name)}" — Schüler-Profile bleiben erhalten`,
    },
    {
      val: 'archivieren',
      label: 'Ich übernehme sie nicht mehr',
      desc: 'Daten bleiben im Archiv lesbar, Klasse wird nicht weiter genutzt',
    },
    {
      val: 'aufgeloest',
      label: 'Klasse wurde aufgelöst',
      desc: 'Schüler wechseln zu anderen Klassen, Klasse existiert nicht mehr',
    },
  ];

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 4px', color: 'var(--ld-text-primary)' }}>
          {k.name}
        </h2>
        <p style={{ fontSize: 13, color: 'var(--ld-text-secondary)', margin: 0 }}>
          {k.faecher_unterrichtet.join(' · ')} · {k.schueler_count} Schüler/innen
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
        {optionen.map(opt => (
          <button key={opt.val} onClick={() => setWahl(opt.val)} style={{
            textAlign: 'left', padding: '14px 16px', borderRadius: 'var(--ld-radius-lg)',
            border: wahl === opt.val ? '2px solid var(--ld-primary)' : '1.5px solid var(--ld-outline-soft)',
            background: wahl === opt.val ? 'var(--ld-primary-light, #EDE9F8)' : 'var(--ld-bg-card)',
            cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <div style={{
              width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 1,
              border: wahl === opt.val ? '6px solid var(--ld-primary)' : '2px solid #C7C7CC',
              background: 'white', transition: 'all .15s',
            }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ld-text-primary)', marginBottom: 2 }}>
                {opt.label}
              </div>
              <div style={{ fontSize: 12, color: 'var(--ld-text-secondary)', lineHeight: 1.4 }}>
                {opt.desc}
              </div>
            </div>
          </button>
        ))}
      </div>

      <NavButtons laden={laden} onZurueck={onZurueck} onWeiter={() => onWeiter(wahl)} />
    </div>
  );
}

// ── Screen: Klassen-Promotion ─────────────────────────────────────────────────

function KlassePromotionScreen({
  entry, laden, schuelerLaden, onWeiter, onZurueck, onUpdate,
}: {
  entry: KlasseEntry; laden: boolean; schuelerLaden: boolean;
  onWeiter: () => void; onZurueck: () => void;
  onUpdate: (patch: Partial<KlasseEntry>) => void;
}) {
  const k = entry.klasse;
  const aktiv  = entry.schueler.filter(s => s.aktiv);
  const inaktiv = entry.schueler.filter(s => !s.aktiv);

  function toggleFach(fach: string) {
    const neu = entry.gewaehlte_faecher.includes(fach)
      ? entry.gewaehlte_faecher.filter(f => f !== fach)
      : [...entry.gewaehlte_faecher, fach];
    onUpdate({ gewaehlte_faecher: neu });
  }

  function toggleSchueler(id: number) {
    onUpdate({
      schueler: entry.schueler.map(s => s.id === id ? { ...s, aktiv: !s.aktiv } : s),
    });
  }

  const kannWeiter = entry.neuer_name.trim().length > 0 && entry.gewaehlte_faecher.length > 0;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 4px', color: 'var(--ld-text-primary)' }}>
          {k.name} → neuer Name
        </h2>
        <p style={{ fontSize: 13, color: 'var(--ld-text-secondary)', margin: 0 }}>
          Schuljahr wechselt zu {entry.klasse.schuljahr.replace(/(\d+)\/(\d+)/, (_, a, b) => `${Number(a)+1}/${Number(b)+1}`)}
        </p>
      </div>

      {/* Neuer Name */}
      <Section label="Neuer Klassen-Name">
        <input
          value={entry.neuer_name}
          onChange={e => onUpdate({ neuer_name: e.target.value })}
          placeholder="z.B. 10a"
          style={inputStyle}
        />
      </Section>

      {/* Fächer */}
      <Section label={`Welche Fächer übernimmst du? (${entry.gewaehlte_faecher.length}/${k.faecher_unterrichtet.length})`}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {k.faecher_unterrichtet.map(fach => {
            const an = entry.gewaehlte_faecher.includes(fach);
            return (
              <button key={fach} onClick={() => toggleFach(fach)} style={{
                padding: '8px 14px', borderRadius: 'var(--ld-radius-md)', fontSize: 13, fontWeight: 600,
                border: an ? '2px solid var(--ld-primary)' : '1.5px solid var(--ld-outline-soft)',
                background: an ? 'var(--ld-primary-light, #EDE9F8)' : 'var(--ld-bg-input)',
                color: an ? 'var(--ld-primary)' : 'var(--ld-text-secondary)',
                cursor: 'pointer',
              }}>
                {an && <MIcon name="check" size={15} />}{fach}
              </button>
            );
          })}
        </div>
      </Section>

      {/* Schülerliste */}
      <Section label={`Schüler/innen übernehmen (${aktiv.length} von ${entry.schueler.length})`}>
        {schuelerLaden ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--ld-text-secondary)', fontSize: 13 }}>
            Schülerliste wird geladen …
          </div>
        ) : entry.schueler.length === 0 ? (
          <div style={{ padding: '12px 0', color: 'var(--ld-text-secondary)', fontSize: 13 }}>
            Keine Schüler in dieser Klasse.
          </div>
        ) : (
          <div style={{ border: '1.5px solid var(--ld-outline-soft)', borderRadius: 'var(--ld-radius-md)', overflow: 'hidden', maxHeight: 280, overflowY: 'auto' }}>
            {aktiv.map((s, i) => (
              <SchuelerRow key={s.id} s={s} borderedBottom={i < aktiv.length - 1 || inaktiv.length > 0} onToggle={() => toggleSchueler(s.id)} />
            ))}
            {inaktiv.length > 0 && (
              <>
                <div style={{ padding: '6px 14px', background: '#F9F9FB', fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: 0.3 }}>
                  AUSGENOMMEN ({inaktiv.length})
                </div>
                {inaktiv.map((s, i) => (
                  <SchuelerRow key={s.id} s={s} borderedBottom={i < inaktiv.length - 1} onToggle={() => toggleSchueler(s.id)} />
                ))}
              </>
            )}
          </div>
        )}
      </Section>

      <NavButtons laden={laden} onZurueck={onZurueck} onWeiter={onWeiter} weiterDisabled={!kannWeiter} />
    </div>
  );
}

function SchuelerRow({ s, borderedBottom, onToggle }: { s: SchuelerToggle; borderedBottom: boolean; onToggle: () => void }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer',
      borderBottom: borderedBottom ? '1px solid #F0F0F5' : 'none',
      background: s.aktiv ? 'white' : '#FAFAFA',
    }}>
      <input
        type="checkbox" checked={s.aktiv} onChange={onToggle}
        style={{ width: 17, height: 17, accentColor: 'var(--ld-primary)', flexShrink: 0 }}
      />
      <span style={{ fontSize: 14, color: s.aktiv ? 'var(--ld-text-primary)' : 'var(--ld-text-tertiary)' }}>
        {s.nachname}, {s.vorname}
      </span>
    </label>
  );
}

// ── Screen: Attestpflicht (Welle J1) ──────────────────────────────────────────
// Erscheint nur, wenn übernommene Schüler eine aktive Attestpflicht haben.
// Default AUS: Fortführung einer belastenden Maßnahme ist eine aktive Entscheidung.

function datumKurz(iso: string | null): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
  const [j, m, t] = iso.slice(0, 10).split('-');
  return `${t}.${m}.${j}`;
}

function AttestScreen({ liste, laden, onToggle, onWeiter }: {
  liste: AttestEntscheidung[]; laden: boolean;
  onToggle: (id: number) => void; onWeiter: () => void;
}) {
  const neuesSj = schuljahrFensterAusLabel(liste[0]?.schuljahr ?? '');
  const altesSj = schuljahrFensterAusLabel(String(Number(neuesSj.von.slice(0, 4)) - 1));
  const anzahlFort = liste.filter(s => s.fortfuehren).length;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 6px', color: 'var(--ld-text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <MIcon name="medical_services" size={22} color="var(--ld-primary)" />
          Attestpflichten für {neuesSj.label}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--ld-text-secondary)', margin: 0, lineHeight: 1.5 }}>
          {liste.length === 1 ? '1 übernommene Person hat' : `${liste.length} übernommene Schüler/innen haben`} eine aktive Attestpflicht.
          Entscheide einzeln, ob sie im neuen Schuljahr weiterläuft.
        </p>
      </div>

      <div style={{
        display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', marginBottom: 20,
        background: 'var(--ld-primary-light, #EDE9F8)', borderRadius: 'var(--ld-radius-md)',
        fontSize: 13, lineHeight: 1.5, color: 'var(--ld-text-primary)',
      }}>
        <MIcon name="info" size={18} color="var(--ld-primary)" style={{ marginTop: 1 }} />
        <span>Nicht fortgeführte Attestpflichten enden mit dem alten Schuljahr. Du kannst sie jederzeit im Schülerprofil neu setzen.</span>
      </div>

      <Section label={`Fortführen (${anzahlFort} von ${liste.length})`}>
        <div style={{ border: '1.5px solid var(--ld-outline-soft)', borderRadius: 'var(--ld-radius-md)', overflow: 'hidden', background: 'white' }}>
          {liste.map((s, i) => {
            const seit = datumKurz(s.attestpflicht_seit);
            const meta = [s.klasse_name, seit ? `seit ${seit}` : null, s.attestpflicht_notiz].filter(Boolean).join(' · ');
            const toggle = () => onToggle(s.id);
            return (
              <div key={s.id} style={{ padding: '12px 14px', borderBottom: i < liste.length - 1 ? '1px solid #F0F0F5' : 'none' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ld-text-primary)' }}>
                  {s.nachname}, {s.vorname}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ld-text-secondary)', marginTop: 2, overflowWrap: 'anywhere' }}>{meta}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 10, cursor: 'pointer' }} onClick={toggle}>
                  <span style={{ fontSize: 13, color: 'var(--ld-text-primary)' }}>
                    Im Schuljahr {neuesSj.label} fortführen
                    <span style={{ display: 'block', fontSize: 12, marginTop: 1, color: s.fortfuehren ? 'var(--ld-success)' : 'var(--ld-text-tertiary)', fontWeight: 600 }}>
                      {s.fortfuehren ? 'Läuft weiter' : `Endet mit ${altesSj.label}`}
                    </span>
                  </span>
                  <div role="switch" aria-checked={s.fortfuehren} tabIndex={0}
                    aria-label={`${s.vorname} ${s.nachname}: Im Schuljahr ${neuesSj.label} fortführen`}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                    style={{ width: 44, height: 24, borderRadius: 'var(--ld-radius-pill)', background: s.fortfuehren ? 'var(--ld-success)' : '#E5E5EA', position: 'relative', transition: 'background .2s', cursor: 'pointer', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 2, left: s.fortfuehren ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left .2s' }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <NavButtons laden={laden} onZurueck={null} onWeiter={onWeiter} />
    </div>
  );
}

// ── Screen: Stundenplan ───────────────────────────────────────────────────────

function StundenplanScreen({ naechstes_sj, laden, onJetzt, onSpaeter }: {
  naechstes_sj: string; laden: boolean;
  onJetzt: () => void; onSpaeter: () => void;
}) {
  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ marginBottom: 12, color: 'var(--ld-primary)' }}><MIcon name="event" size={44} /></div>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 8px', color: 'var(--ld-text-primary)' }}>
          Stundenplan für {naechstes_sj}
        </h2>
        <p style={{ fontSize: 14, color: 'var(--ld-text-secondary)', margin: 0, lineHeight: 1.5 }}>
          Die alten Stundenpläne bleiben im Archiv.<br />Für neue Klassen ist der Stundenplan noch leer.
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button onClick={onJetzt} disabled={laden} style={{
          padding: '15px 0', borderRadius: 'var(--ld-radius-lg)', border: 'none',
          background: 'var(--ld-primary)', color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer',
        }}>
          Jetzt Stundenplan erstellen →
        </button>
        <button onClick={onSpaeter} disabled={laden} style={{
          padding: '15px 0', borderRadius: 'var(--ld-radius-lg)', border: '1.5px solid var(--ld-outline-soft)',
          background: 'var(--ld-bg-input)', color: 'var(--ld-text-secondary)', fontSize: 15, fontWeight: 600, cursor: 'pointer',
        }}>
          Später erledigen
        </button>
      </div>
    </div>
  );
}

// ── Screen: Bestätigung ───────────────────────────────────────────────────────

function BestaetigungScreen({ summary, naechstes_sj, laden, onFertig }: {
  summary: WizardSummary; naechstes_sj: string; laden: boolean; onFertig: () => void;
}) {
  const zeilen = [
    { label: 'Klassen promoviert', val: summary.promotioniert, icon: 'check' },
    { label: 'Klassen archiviert',  val: summary.archiviert,   icon: 'check' },
    { label: 'Klassen aufgelöst',   val: summary.aufgeloest,   icon: 'check' },
    { label: 'Anker-Noten übertragen', val: summary.anker_noten_transferiert, icon: 'check' },
  ].filter(z => (z.val ?? 0) > 0);
  const attestFort    = summary.attest_fortgefuehrt ?? 0;
  const attestBeendet = summary.attest_beendet ?? 0;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ marginBottom: 12, color: 'var(--ld-success-text)' }}><MIcon name="task_alt" size={48} /></div>
        <h2 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 8px', color: 'var(--ld-text-primary)' }}>
          Schuljahr {naechstes_sj} ist aktiv!
        </h2>
      </div>

      <div style={{ background: 'var(--ld-bg-card)', borderRadius: 'var(--ld-radius-lg)', padding: '16px 20px', marginBottom: 28, boxShadow: 'var(--ld-shadow-card)' }}>
        {zeilen.length === 0 && attestFort + attestBeendet === 0 ? (
          <p style={{ margin: 0, fontSize: 14, color: 'var(--ld-text-secondary)' }}>Übergang abgeschlossen.</p>
        ) : zeilen.map(z => (
          <div key={z.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F0F0F5', fontSize: 14 }}>
            <MIcon name={z.icon} size={18} color="var(--ld-success-text)" />
            <span style={{ color: 'var(--ld-text-primary)', fontWeight: 600 }}>{z.val}</span>
            <span style={{ color: 'var(--ld-text-secondary)' }}>{z.label}</span>
          </div>
        ))}
        {attestFort + attestBeendet > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #F0F0F5', fontSize: 14 }}>
            <MIcon name="check" size={18} color="var(--ld-success-text)" />
            <span style={{ color: 'var(--ld-text-secondary)' }}>
              Attestpflicht: <strong style={{ color: 'var(--ld-text-primary)' }}>{attestFort}</strong> fortgeführt, <strong style={{ color: 'var(--ld-text-primary)' }}>{attestBeendet}</strong> beendet
            </span>
          </div>
        )}
      </div>

      <button onClick={onFertig} disabled={laden} style={{
        width: '100%', padding: '15px 0', borderRadius: 'var(--ld-radius-lg)', border: 'none',
        background: 'var(--ld-primary)', color: 'white', fontSize: 16, fontWeight: 700, cursor: 'pointer',
      }}>
        Fertig — zum Dashboard
      </button>
    </div>
  );
}

// ── Shared UI pieces ──────────────────────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ld-text-secondary)', letterSpacing: 0.4, marginBottom: 8 }}>
        {label.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '11px 14px', borderRadius: 'var(--ld-radius-md)',
  border: '1.5px solid var(--ld-outline-soft)', fontSize: 15, outline: 'none',
  boxSizing: 'border-box', color: 'var(--ld-text-primary)', background: 'var(--ld-bg-input)',
};

function NavButtons({ laden, onZurueck, onWeiter, weiterDisabled = false }: {
  laden: boolean; weiterDisabled?: boolean;
  onZurueck: (() => void) | null; onWeiter: () => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
      {onZurueck && (
        <button onClick={onZurueck} disabled={laden} style={{
          padding: '13px 20px', borderRadius: 'var(--ld-radius-lg)', border: '1.5px solid var(--ld-outline-soft)',
          background: 'var(--ld-bg-input)', color: 'var(--ld-text-secondary)', fontSize: 15, fontWeight: 600, cursor: laden ? 'default' : 'pointer',
        }}>
          ← Zurück
        </button>
      )}
      <button onClick={onWeiter} disabled={laden || weiterDisabled} style={{
        flex: 1, padding: '13px 0', borderRadius: 'var(--ld-radius-lg)', border: 'none',
        background: laden || weiterDisabled ? '#D0CAEC' : 'var(--ld-primary)',
        color: 'white', fontSize: 15, fontWeight: 700, cursor: laden || weiterDisabled ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      }}>
        {laden
          ? <span style={{ width: 17, height: 17, border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin .6s linear infinite' }} />
          : 'Weiter →'
        }
      </button>
    </div>
  );
}

// ── Main: UebergangWizard ─────────────────────────────────────────────────────

export function UebergangWizard({
  onFertig,
  onNavStundenplan,
}: {
  onFertig: () => void;
  onNavStundenplan: () => void;
}) {
  useOhneMikroKnopf(); // Welle B1: Vollbild-Assistent — der Knopf lag auf „Weiter“
  const [schritt, setSchritt]   = useState<Schritt>('laden');
  const [logId, setLogId]       = useState<number | null>(null);
  const [aktuellesSj, setAktuellesSj] = useState('');
  const [naechstesSj, setNaechstesSj] = useState('');
  const [klassen, setKlassen]   = useState<KlasseEntry[]>([]);
  const [idx, setIdx]           = useState(0);
  const [laden, setLaden]       = useState(false);
  const [schuelerLaden, setSchuelerLaden] = useState(false);
  const [summary, setSummary]   = useState<WizardSummary>({});
  const [attestListe, setAttestListe] = useState<AttestEntscheidung[]>([]);
  const [attestErgebnis, setAttestErgebnis] = useState<{ fortgefuehrt: number; beendet: number } | null>(null);

  // ── Initialisierung: Status laden ──────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const status = await uebergangApi.status();
        setAktuellesSj(status.aktuelles_schuljahr);
        setNaechstesSj(status.naechstes_schuljahr);
        const eintraege = status.aktive_klassen.map(initKlasseEntry);
        setKlassen(eintraege);

        if (status.wizard_offen && status.log_id) {
          setLogId(status.log_id);
          setSchritt('entscheidung');
          setIdx(0);
        } else {
          setSchritt('begruessung');
        }
      } catch (err: any) {
        toast('Status konnte nicht geladen werden.', 'error');
        setSchritt('begruessung');
      }
    })();
  }, []);

  // ── Schüler für aktuelle Promotion-Klasse laden ────────────────────────────
  useEffect(() => {
    if (schritt !== 'promotion') return;
    const entry = klassen[idx];
    if (!entry || entry.schueler_geladen) return;
    setSchuelerLaden(true);
    uebergangApi.klasseSchueler(entry.klasse.id)
      .then(liste => {
        setKlassen(prev => prev.map((e, i) => i === idx ? {
          ...e,
          schueler: liste.map(s => ({ ...s, aktiv: true })),
          schueler_geladen: true,
        } : e));
      })
      .catch(() => toast('Schüler konnten nicht geladen werden.', 'error'))
      .finally(() => setSchuelerLaden(false));
  }, [schritt, idx]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Klassen-Entry aktualisieren ────────────────────────────────────────────
  const updateEntry = useCallback((patch: Partial<KlasseEntry>) => {
    setKlassen(prev => prev.map((e, i) => i === idx ? { ...e, ...patch } : e));
  }, [idx]);

  // ── Nach der letzten Klasse: Attestpflicht-Schritt nur bei Betroffenen ─────
  async function nachKlassenWeiter(id: number | null) {
    if (id) {
      try {
        const { schueler } = await uebergangApi.attestKandidaten(id);
        if (schueler.length > 0) {
          setAttestListe(schueler.map(s => ({ ...s, fortfuehren: false })));
          setSchritt('attest');
          return;
        }
      } catch {
        toast('Attestpflichten konnten nicht geladen werden — bitte im Schülerprofil prüfen.', 'error');
      }
    }
    setSchritt('stundenplan');
  }

  // ── Handler: Attestpflicht-Entscheidung ausführen ─────────────────────────
  // Beenden über den bestehenden Speicherpfad (PUT /api/schueler/:id → Audit 'update').
  // seit/notiz bleiben als Nachweis stehen; nichts wird gelöscht.
  async function handleAttest() {
    setLaden(true);
    let beendet = attestErgebnis?.beendet ?? 0;
    try {
      for (const s of attestListe.filter(a => !a.fortfuehren)) {
        await apiPut(`/api/schueler/${s.id}`, { attestpflicht: false });
        beendet++;
        setAttestListe(prev => prev.filter(a => a.id !== s.id));
      }
      const fortgefuehrt = attestListe.filter(a => a.fortfuehren).length;
      setAttestErgebnis({ fortgefuehrt, beendet });
      window.dispatchEvent(new CustomEvent('lehrerdesk-schueler-reload'));
      setSchritt('stundenplan');
    } catch (err: any) {
      setAttestErgebnis({ fortgefuehrt: 0, beendet });
      toast(err?.message ?? 'Attestpflicht konnte nicht beendet werden.', 'error');
    } finally {
      setLaden(false);
    }
  }

  // ── Handler: Wizard starten ────────────────────────────────────────────────
  async function handleStart() {
    setLaden(true);
    try {
      const { log_id } = await uebergangApi.start();
      setLogId(log_id);
      if (klassen.length === 0) {
        await nachKlassenWeiter(log_id);
      } else {
        setIdx(0);
        setSchritt('entscheidung');
      }
    } catch (err: any) {
      toast(err.message ?? 'Fehler beim Starten.', 'error');
    } finally {
      setLaden(false);
    }
  }

  // ── Handler: Entscheidung bestätigen ──────────────────────────────────────
  async function handleEntscheidung(wahl: Wahl) {
    updateEntry({ wahl });
    if (wahl === 'promotion') {
      setSchritt('promotion');
      return;
    }
    if (!logId) return;
    setLaden(true);
    try {
      if (wahl === 'archivieren') {
        await uebergangApi.archivieren(klassen[idx].klasse.id, logId);
      } else {
        await uebergangApi.aufloesen(klassen[idx].klasse.id, logId);
      }
      setKlassen(prev => prev.map((e, i) => i === idx ? { ...e, wahl, abgeschlossen: true } : e));
      const next = klassen.findIndex((e, i) => i > idx && !e.abgeschlossen);
      if (next === -1) await nachKlassenWeiter(logId);
      else { setIdx(next); setSchritt('entscheidung'); }
    } catch (err: any) {
      toast(err.message ?? 'Fehler.', 'error');
    } finally {
      setLaden(false);
    }
  }

  // ── Handler: Promotion abschicken ─────────────────────────────────────────
  async function handlePromotion() {
    if (!logId) return;
    const e = klassen[idx];
    setLaden(true);
    try {
      await uebergangApi.promotionieren(e.klasse.id, {
        log_id: logId,
        neuer_name: e.neuer_name.trim(),
        gewaehlte_faecher: e.gewaehlte_faecher,
        uebernommene_schueler_ids: e.schueler.filter(s => s.aktiv).map(s => s.id),
      });
      setSummary(prev => ({
        ...prev,
        promotioniert: (prev.promotioniert ?? 0) + 1,
      }));
      setKlassen(prev => prev.map((entry, i) => i === idx ? { ...entry, abgeschlossen: true } : entry));
      const next = klassen.findIndex((entry, i) => i > idx && !entry.abgeschlossen);
      if (next === -1) await nachKlassenWeiter(logId);
      else { setIdx(next); setSchritt('entscheidung'); }
    } catch (err: any) {
      toast(err.message ?? 'Promotion fehlgeschlagen.', 'error');
    } finally {
      setLaden(false);
    }
  }

  // ── Handler: Abschluss ────────────────────────────────────────────────────
  async function handleAbschluss(navigiereStundenplan = false) {
    if (navigiereStundenplan) {
      if (logId) { try { await uebergangApi.abschluss(logId, attestErgebnis ?? undefined); } catch {} }
      window.dispatchEvent(new CustomEvent('lehrerdesk-klassen-reload'));
      onNavStundenplan();
      return;
    }
    setLaden(true);
    try {
      if (logId) {
        const { summary: srv } = await uebergangApi.abschluss(logId, attestErgebnis ?? undefined);
        setSummary(srv);
      }
    } catch {}
    setSchritt('bestaetigung');
    setLaden(false);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const totalKlassen = klassen.length;

  const content = (() => {
    if (schritt === 'laden') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <span style={{ width: 28, height: 28, border: '3px solid var(--ld-primary)', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin .7s linear infinite' }} />
        </div>
      );
    }
    if (schritt === 'begruessung') {
      const schuelerGesamt = klassen.reduce((s, e) => s + e.klasse.schueler_count, 0);
      return (
        <BegruessungScreen
          aktuelles_sj={aktuellesSj} naechstes_sj={naechstesSj}
          klassen_count={totalKlassen} schueler_count={schuelerGesamt}
          laden={laden} onStart={handleStart}
        />
      );
    }
    if (schritt === 'entscheidung' && klassen[idx]) {
      return (
        <KlasseEntscheidungScreen
          entry={klassen[idx]} laden={laden}
          onWeiter={handleEntscheidung}
          onZurueck={idx === 0 ? () => setSchritt('begruessung') : () => { setIdx(idx - 1); setSchritt('entscheidung'); }}
        />
      );
    }
    if (schritt === 'promotion' && klassen[idx]) {
      return (
        <KlassePromotionScreen
          entry={klassen[idx]} laden={laden} schuelerLaden={schuelerLaden}
          onWeiter={handlePromotion}
          onZurueck={() => setSchritt('entscheidung')}
          onUpdate={updateEntry}
        />
      );
    }
    if (schritt === 'attest' && attestListe.length > 0) {
      return (
        <AttestScreen
          liste={attestListe} laden={laden}
          onToggle={id => setAttestListe(prev => prev.map(a => a.id === id ? { ...a, fortfuehren: !a.fortfuehren } : a))}
          onWeiter={handleAttest}
        />
      );
    }
    if (schritt === 'stundenplan') {
      return (
        <StundenplanScreen
          naechstes_sj={naechstesSj} laden={laden}
          onJetzt={() => handleAbschluss(true)}
          onSpaeter={() => handleAbschluss(false)}
        />
      );
    }
    if (schritt === 'bestaetigung') {
      return (
        <BestaetigungScreen
          summary={summary} naechstes_sj={naechstesSj} laden={laden}
          onFertig={() => {
            window.dispatchEvent(new CustomEvent('lehrerdesk-klassen-reload'));
            onFertig();
          }}
        />
      );
    }
    return null;
  })();

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'var(--ld-bg-app)',
      display: 'flex', flexDirection: 'column',
      overflowY: 'auto',
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', borderBottom: '1px solid var(--ld-outline-soft)',
        background: 'white', position: 'sticky', top: 0, zIndex: 1,
      }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--ld-text-primary)' }}>
          Schuljahres-Übergang
        </div>
        <button onClick={async () => {
          if (schritt === 'bestaetigung' || schritt === 'laden' || schritt === 'begruessung') {
            onFertig();
            return;
          }
          if (await confirm('Wizard abbrechen? Dein Fortschritt bleibt gespeichert — du kannst später weitermachen.', { confirmLabel: 'Abbrechen', cancelLabel: 'Weiter' })) {
            onFertig();
          }
        }} style={{
          width: 36, height: 36, borderRadius: '50%', border: 'none',
          background: 'var(--ld-bg-app)', cursor: 'pointer',
          fontSize: 18, color: 'var(--ld-text-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          ×
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '24px 20px 40px', maxWidth: 520, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        <WizardStepper idx={idx} gesamt={totalKlassen} schritt={schritt} />
        {content}
      </div>
    </div>
  );
}
